import React, { useEffect, useState } from "react";
import { Form, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import bmsService from "../../features/bms/bmsService";
import { TbSettings, TbDeviceFloppy, TbClock, TbCalendarEvent, TbShieldX } from "react-icons/tb";
import BmsChatbotSelector from "../../components/bms/BmsChatbotSelector";
import { showSuccess, showError } from "../../utils/bmsToast";

const BmsBookingConfigurationsPage: React.FC = () => {
  const { t } = useTranslation();
  const [chatbotId, setChatbotId] = useState<number | null>(null);
  const [settings, setSettings] = useState({
    lead_time_minutes: 60,
    slot_interval_minutes: 30,
    cancellation_window_minutes: 120,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!chatbotId) return;
      setIsLoading(true);
      try {
        const res = await bmsService.getBookingSettings(chatbotId);
        if (res?.data) {
          setSettings({
            lead_time_minutes: res.data.lead_time_minutes ?? 60,
            slot_interval_minutes: res.data.slot_interval_minutes ?? 30,
            cancellation_window_minutes: res.data.cancellation_window_minutes ?? 120,
          });
        }
      } catch (e: any) {
        showError(e?.response?.data?.detail || e?.message || "Failed to load booking settings");
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [chatbotId]);

  const save = async () => {
    if (!chatbotId) return;
    setIsSaving(true);
    try {
      await bmsService.updateBookingSettings(chatbotId, settings);
      showSuccess(t("bms_settings_saved"));
    } catch (e: any) {
      showError(e?.response?.data?.detail || e?.message || "Failed to save booking settings");
    } finally {
      setIsSaving(false);
    }
  };

  const configFields = [
    {
      key: "lead_time_minutes" as const,
      icon: <TbClock />,
      label: t("bms_lead_time"),
      hint: "Minimum time before a booking can be made",
      min: 0,
      max: 10080,
    },
    {
      key: "slot_interval_minutes" as const,
      icon: <TbCalendarEvent />,
      label: t("bms_slot_interval"),
      hint: "Interval between available time slots",
      min: 5,
      max: 480,
    },
    {
      key: "cancellation_window_minutes" as const,
      icon: <TbShieldX />,
      label: t("bms_cancellation_window"),
      hint: "How close to the appointment a cancellation is allowed",
      min: 0,
      max: 10080,
    },
  ];

  return (
    <div className="chatbots-page bms-page">
      <div className="page-header-card mb-3">
        <div className="page-header-text">
          <h1 className="d-flex align-items-center gap-2"><TbSettings /> {t("bms_booking_configurations")}</h1>
          <p>{t("bms_booking_configurations_desc")}</p>
        </div>
      </div>

      <div className="p-3 pt-0">
        <BmsChatbotSelector chatbotId={chatbotId} onChatbotChange={setChatbotId} />

        {isLoading ? (
          <div className="d-flex justify-content-center py-5"><Spinner animation="border" variant="primary" /></div>
        ) : (
          <div className="bms-form-card bms-config-form-card mt-3">
            <div className="bms-form-header">
              <div className="bms-form-icon"><TbSettings /></div>
              <div>
                <h5>{t("bms_booking_configurations")}</h5>
                <p>{t("bms_booking_configurations_desc")}</p>
              </div>
            </div>

            <div className="bms-form-body">
              {configFields.map((field) => (
                <div className="bms-form-row bms-config-form-row" key={field.key}>
                  <div className="bms-form-label-col">
                    <label className="d-flex align-items-center gap-2">
                      <span style={{ color: "var(--brand-primary)", fontSize: "1.1rem" }}>{field.icon}</span>
                      {field.label}
                    </label>
                    <div className="bms-form-hint">{field.hint}</div>
                  </div>
                  <div className="bms-form-input-col bms-config-input-col">
                    <Form.Control
                      className="bms-config-input"
                      type="number"
                      min={field.min}
                      max={field.max}
                      value={settings[field.key]}
                      onChange={(e) => setSettings((prev) => ({ ...prev, [field.key]: Number(e.target.value) }))}
                      disabled={!chatbotId}
                    />
                    <div className="bms-config-input-hint">{t("bms_minutes_short")}</div>
                  </div>
                </div>
              ))}

              <div className="d-flex justify-content-end mt-4 pt-3 bms-config-save-row" style={{ borderTop: "1px solid var(--border-subtle)" }}>
                <button className="bms-btn-primary" onClick={save} disabled={!chatbotId || isSaving}>
                  {isSaving ? <Spinner size="sm" animation="border" /> : <TbDeviceFloppy />}
                  {t("bms_save")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BmsBookingConfigurationsPage;
